/**
 * MarketDetailWithWatchlist + MarketDetail — Extracted from DetailPage.jsx (A12 Sprint 3).
 *
 * Watchlist tab with AI recommendations + "All Markets" tab delegating to MarketDetail.
 * MarketDetail co-extracted as private helper (only used by MarketDetailWithWatchlist).
 * Smallest-first extraction per Cross-Block Pattern 4.
 *
 * A6-26 fix: raw fetch → apiClient.get
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';

const TOOLTIP_STYLE = {
  background: 'var(--aif-bg-elevated)',
  border: '1px solid var(--aif-border-default)',
  borderRadius: 12,
  fontSize: 12,
};
const AXIS_TICK = { fontSize: 11, fill: 'var(--aif-text-muted)' };

// ─── MarketDetail (Sprint 13 — real data) ────────────────────
function MarketDetail({ market, token }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState('30');
  const [historyData, setHistoryData] = useState(null);
  const [selectedCommodity, setSelectedCommodity] = useState(null);

  const prices = market?.prices || [];
  const hasData = market?.source === 'live' && prices.length > 0;

  // Fetch history when a commodity is selected
  const fetchHistory = async (code, days) => {
    if (!token || !code) return;
    try {
      const data = await apiClient.get(`/api/v1/aif/market/prices/${code}?days=${days}`);
      setHistoryData(data);
    } catch { /* ignore */ }
  };

  // Auto-select first commodity
  if (!selectedCommodity && prices.length > 0) {
    const first = prices[0]?.commodity_code;
    if (first) {
      setSelectedCommodity(first);
      fetchHistory(first, period);
    }
  }

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="text-4xl mb-4">📊</div>
        <h3 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-2">
          {t('dashboard.marketLoading')}
        </h3>
        <p className="text-xs text-[var(--aif-text-muted)] max-w-[250px]">
          {t('detail.marketSetup')}
        </p>
      </div>
    );
  }

  // Group prices by category
  const categories = { crops: [], milk: [], livestock: [], inputs: [] };
  prices.forEach(p => {
    const cat = p.category || 'crops';
    if (categories[cat]) categories[cat].push(p);
  });

  const catLabels = {
    crops: t('detail.commodityCrops'),
    milk: t('detail.commodityMilk'),
    livestock: t('detail.commodityLivestock'),
    inputs: t('detail.commodityInputs'),
  };

  const chartData = historyData?.prices?.map(p => ({
    date: p.date ? new Date(p.date).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' }) : '',
    price: p.price,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Period tabs */}
      <div className="flex gap-2">
        {[{ key: '30', label: t('detail.period30d') }, { key: '90', label: t('detail.period90d') }].map(tab => (
          <button
            key={tab.key}
            onClick={() => { setPeriod(tab.key); if (selectedCommodity) fetchHistory(selectedCommodity, tab.key); }}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
              period === tab.key
                ? 'bg-[var(--aif-color-primary)] text-white'
                : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Chart */}
      {chartData.length > 0 && historyData && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] uppercase tracking-wide">
              {historyData.commodity_name}
            </h3>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[var(--aif-text-muted)]">{t('detail.avg90d')}: {historyData.avg_period} {historyData.unit}</span>
              {historyData.above_average && (
                <span className="text-green-400 font-medium">{t('detail.aboveAvg')}</span>
              )}
              {historyData.above_average === false && (
                <span className="text-red-400 font-medium">{t('detail.belowAvg')}</span>
              )}
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--aif-border-default)" />
                <XAxis dataKey="date" tick={AXIS_TICK} interval={Math.max(1, Math.floor(chartData.length / 6))} />
                <YAxis tick={AXIS_TICK} domain={['auto', 'auto']} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                {historyData.avg_period && (
                  <ReferenceLine y={historyData.avg_period} stroke="#94a3b8" strokeDasharray="6 3" label={{ value: `⌀ ${historyData.avg_period}`, position: 'right', fill: '#94a3b8', fontSize: 10 }} />
                )}
                <Line type="monotone" dataKey="price" stroke="var(--aif-color-primary)" strokeWidth={2} dot={false} name={historyData.unit} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* All prices by category */}
      {/* Source + last-updated footer */}
      {prices.length > 0 && (() => {
        const mostRecent = prices
          .filter(p => p.recorded_at)
          .reduce((best, p) => {
            const t = new Date(p.recorded_at).getTime();
            return t > best ? t : best;
          }, 0);
        const dateStr = mostRecent
          ? new Date(mostRecent).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
          : null;
        return dateStr ? (
          <div className="text-[10px] text-[var(--aif-text-muted)] text-center pb-1">
            {t('ui.updated')}: {dateStr}
          </div>
        ) : null;
      })()}

      {Object.entries(categories).map(([cat, items]) => {
        if (!items.length) return null;
        return (
          <div key={cat}>
            <h3 className="text-xs font-semibold text-[var(--aif-text-secondary)] mb-2 uppercase tracking-wide">
              {catLabels[cat]}
            </h3>
            <div className="space-y-1">
              {items.map(p => {
                const updatedAt = p.recorded_at
                  ? new Date(p.recorded_at).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })
                  : null;
                return (
                  <button
                    key={p.commodity_code}
                    onClick={() => { setSelectedCommodity(p.commodity_code); fetchHistory(p.commodity_code, period); }}
                    className={`w-full flex items-center justify-between py-3 px-3 rounded-xl border transition text-left ${
                      selectedCommodity === p.commodity_code
                        ? 'border-[var(--aif-color-primary)]/50 bg-[var(--aif-color-primary)]/5'
                        : 'border-transparent hover:bg-[var(--aif-bg-surface)]'
                    }`}
                  >
                    <div>
                      <span className="text-sm text-[var(--aif-text-primary)]">{p.commodity_name}</span>
                      {p.source && (
                        <div className="text-[10px] text-[var(--aif-text-muted)]">{p.source}{updatedAt ? ` · ${updatedAt}` : ''}</div>
                      )}
                      {!p.source && updatedAt && (
                        <div className="text-[10px] text-[var(--aif-text-muted)]">📈 {updatedAt}</div>
                      )}
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{p.price} {p.unit}</span>
                      {p.change_pct != null && (
                        <span className={`text-xs ${p.trend === 'up' ? 'text-green-400' : p.trend === 'down' ? 'text-red-400' : 'text-[var(--aif-text-muted)]'}`}>
                          {p.trend === 'up' ? '↑' : p.trend === 'down' ? '↓' : '→'}{p.change_pct > 0 ? '+' : ''}{p.change_pct}%
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Market Detail with Watchlist (Sprint 18c) ────────────────
export default function MarketDetailWithWatchlist({ market, token }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('watchlist');
  const [watchlist, setWatchlist] = useState(null);

  useEffect(() => {
    if (!token) return;
    apiClient.get('/api/v1/aif/market/watchlist')
      .then(d => setWatchlist(d))
      .catch(() => {});
  }, [token]);

  const SIGNAL_COLORS = { sell: 'text-green-400', hold: 'text-yellow-400', wait: 'text-blue-400', no_data: 'text-[var(--aif-text-muted)]' };
  const SIGNAL_LABELS = { sell: t('watchlist.sell'), hold: t('watchlist.hold'), wait: t('watchlist.wait'), no_data: '—' };

  const hasLiveData = market?.source === 'live' && (market?.prices?.length || 0) > 0;

  return (
    <div className="space-y-4">
      {/* Tab switcher + live badge */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setTab('watchlist')}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
              tab === 'watchlist' ? 'bg-[var(--aif-color-primary)] text-white' : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]'
            }`}
          >
            {t('watchlist.title')}
          </button>
          <button
            onClick={() => setTab('all')}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
              tab === 'all' ? 'bg-[var(--aif-color-primary)] text-white' : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]'
            }`}
          >
            {t('watchlist.allMarkets')}
          </button>
        </div>
        {hasLiveData && (
          <span className="text-[10px] font-medium text-blue-400 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block animate-pulse" />
            {t('watchlist.live')}
          </span>
        )}
      </div>

      {tab === 'watchlist' && (
        <div className="space-y-3">
          {watchlist?.watchlist?.length > 0 ? (
            watchlist.watchlist.map(item => {
              const rec = item.recommendation || {};
              let signal = rec.signal || 'no_data';
              let reason = rec.reason_de || rec.reason_en || '';
              // If no real recommendation, generate from trend
              if (signal === 'no_data' || !reason) {
                const change = item.change_7d_pct || 0;
                if (change > 2) {
                  signal = 'sell_watch';
                  reason = t('watchlist.trendUpReason');
                } else if (change < -2) {
                  signal = 'hold';
                  reason = t('watchlist.trendDownReason');
                } else {
                  signal = 'hold';
                  reason = t('watchlist.trendStableReason');
                }
              }
              const vsAvg90 = item.latest_price && item.avg_90d
                ? (((item.latest_price - item.avg_90d) / item.avg_90d) * 100).toFixed(1)
                : null;
              return (
                <div key={item.commodity_code} className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <h4 className="text-sm font-semibold text-[var(--aif-text-primary)]">{item.commodity_name}</h4>
                      {item.avg_90d && (
                        <span className="text-[10px] text-[var(--aif-text-muted)]">
                          ⌀ 90T: {item.avg_90d.toFixed(2)} {item.unit}
                          {vsAvg90 !== null && (
                            <span className={`ml-1 font-medium ${parseFloat(vsAvg90) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                              ({parseFloat(vsAvg90) > 0 ? '+' : ''}{vsAvg90}%)
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {item.latest_price ? (
                        <span className="text-sm font-bold text-[var(--aif-text-primary)]">
                          {item.latest_price.toFixed(2)} {item.unit}
                        </span>
                      ) : (
                        <span className="text-xs text-[var(--aif-text-muted)]">{t('watchlist.noPrice')}</span>
                      )}
                      {item.change_7d_pct != null && (
                        <span className={`text-xs ${item.trend === 'up' ? 'text-green-400' : item.trend === 'down' ? 'text-red-400' : 'text-[var(--aif-text-muted)]'}`}>
                          {item.trend === 'up' ? '↑' : item.trend === 'down' ? '↓' : '→'}{item.change_7d_pct > 0 ? '+' : ''}{item.change_7d_pct}%
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Inventory + value */}
                  {item.inventory_qty > 0 && (
                    <div className="text-xs text-[var(--aif-text-muted)] mb-1">
                      {item.inventory_qty.toFixed(0)} {item.inventory_unit} {t('watchlist.inStorage')}
                      {item.inventory_value && <span className="ml-1 font-medium text-[var(--aif-text-primary)]">= {item.inventory_value.toLocaleString()} EUR</span>}
                    </div>
                  )}
                  {/* AI recommendation */}
                  {signal !== 'no_data' && (
                    <div className="mt-2 px-3 py-2 rounded-xl bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)]">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[10px] uppercase font-medium text-[var(--aif-text-muted)]">{t('watchlist.recommendation')}</span>
                        <span className={`text-xs font-bold ${SIGNAL_COLORS[signal]}`}>{SIGNAL_LABELS[signal]}</span>
                      </div>
                      <p className="text-[11px] text-[var(--aif-text-secondary)] leading-relaxed">{reason}</p>
                    </div>
                  )}
                  {/* Alert badge */}
                  {item.has_alert && (
                    <div className="mt-1.5 text-[10px] text-blue-400 font-medium">{t('watchlist.alertActive')}</div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-sm text-[var(--aif-text-muted)]">{t('watchlist.noData')}</div>
          )}
        </div>
      )}

      {tab === 'all' && <MarketDetail market={market} token={token} />}
    </div>
  );
}
